import React, { useState, useCallback, useEffect } from 'react';
import { Copy, Check, Link2, Shield, Clock, Lock, Trash2, Globe, X, Loader2, Eye, Edit } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog';
import { Button } from './Button';
import { Input } from './Input';

interface ShareAssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  assetType: 'media' | 'attachment' | 'folder';
  assetName: string;
}

interface ShareLink {
  id: string;
  shareToken: string;
  shareUrl: string;
  permission: string;
  expiresAt: string | null;
  maxDownloads: number | null;
  downloadCount: number;
  hasPassword: boolean;
  createdAt: string;
}

export function ShareAssetDialog({ open, onOpenChange, assetId, assetType, assetName }: ShareAssetDialogProps) {
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Create form state
  const [permission, setPermission] = useState<'view' | 'download'>('view');
  const [expiresInDays, setExpiresInDays] = useState('7');
  const [maxDownloads, setMaxDownloads] = useState('');
  const [password, setPassword] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/_api/user-media/share-link', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list', assetType, assetId }),
      });
      if (res.ok) {
        const data = await res.json();
        setLinks(data.links || []);
      }
    } catch (e) {
      console.error('[ShareAssetDialog] fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [assetId, assetType]);

  useEffect(() => {
    if (open) fetchLinks();
  }, [open, fetchLinks]);

  const handleCreate = useCallback(async () => {
    setCreating(true);
    try {
      const res = await fetch('/_api/user-media/share-link', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          assetType,
          assetId,
          permission,
          expiresInDays: expiresInDays ? Number(expiresInDays) : undefined,
          maxDownloads: maxDownloads ? Number(maxDownloads) : undefined,
          password: password || undefined,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        // Copy immediately
        navigator.clipboard.writeText(data.shareUrl);
        toast.success('Share link created and copied to clipboard');
        setShowCreateForm(false);
        setPassword('');
        setMaxDownloads('');
        fetchLinks();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Failed to create share link');
      }
    } catch (e: any) {
      toast.error(e.message || 'Failed to create share link');
    } finally {
      setCreating(false);
    }
  }, [assetId, assetType, permission, expiresInDays, maxDownloads, password, fetchLinks]);

  const handleRevoke = useCallback(async (linkId: string) => {
    if (!confirm('Revoke this share link? Anyone with the link will lose access.')) return;
    try {
      await fetch('/_api/user-media/share-link', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'revoke', linkId }),
      });
      toast.success('Share link revoked');
      fetchLinks();
    } catch (e: any) {
      toast.error(e.message || 'Failed to revoke link');
    }
  }, [fetchLinks]);

  const copyLink = useCallback((link: ShareLink) => {
    navigator.clipboard.writeText(link.shareUrl);
    setCopiedId(link.id);
    toast.success('Link copied');
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ maxWidth: 480 }}>
        <DialogHeader>
          <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Link2 size={18} />
            Share
          </DialogTitle>
          <DialogDescription style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {assetName}
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
          {/* Create new link */}
          {!showCreateForm ? (
            <button
              onClick={() => setShowCreateForm(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '12px 16px', borderRadius: 10, border: '1px dashed var(--border)',
                background: 'transparent', color: 'var(--primary)', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.15s ease',
              }}
            >
              <Globe size={16} />
              Create Share Link
            </button>
          ) : (
            <div style={{
              padding: 16, borderRadius: 10, border: '1px solid var(--border)',
              background: 'var(--muted)', display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                    <Shield size={11} /> Permission
                  </label>
                  <select
                    value={permission}
                    onChange={(e) => setPermission(e.target.value as any)}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)',
                      background: 'var(--background)', color: 'var(--foreground)', fontSize: '0.8rem',
                    }}
                  >
                    <option value="view">View Only</option>
                    <option value="download">Download</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                    <Clock size={11} /> Expires In
                  </label>
                  <select
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)',
                      background: 'var(--background)', color: 'var(--foreground)', fontSize: '0.8rem',
                    }}
                  >
                    <option value="1">1 Day</option>
                    <option value="7">7 Days</option>
                    <option value="30">30 Days</option>
                    <option value="90">90 Days</option>
                    <option value="">Never</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                    <Lock size={11} /> Password (optional)
                  </label>
                  <Input
                    type="password"
                    placeholder="No password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ fontSize: '0.8rem' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                    Max Downloads
                  </label>
                  <Input
                    type="number"
                    placeholder="Unlimited"
                    value={maxDownloads}
                    onChange={(e) => setMaxDownloads(e.target.value)}
                    min="1"
                    style={{ fontSize: '0.8rem' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="ghost" size="sm" onClick={() => { setShowCreateForm(false); setPassword(''); setMaxDownloads(''); }}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleCreate} disabled={creating}>
                  {creating ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Creating...</> : 'Create & Copy Link'}
                </Button>
              </div>
            </div>
          )}

          {/* Existing links */}
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', color: 'var(--muted-foreground)' }}>
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : links.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--muted-foreground)', fontSize: '0.8rem' }}>
              <Link2 size={24} style={{ opacity: 0.3, margin: '0 auto 6px' }} />
              <p>No active share links</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted-foreground)' }}>
                Active Links ({links.length})
              </div>
              {links.map((link) => (
                <div key={link.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  borderRadius: 8, border: '1px solid var(--border)', background: 'var(--background)',
                }}>
                  <Globe size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {link.shareUrl.replace('https://tasking.tech/shared/', '.../')}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.62rem', color: 'var(--muted-foreground)', marginTop: 2, flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '1px 6px', borderRadius: 999, fontSize: '0.58rem', fontWeight: 600,
                        background: link.permission === 'download'
                          ? 'color-mix(in srgb, #3b82f6 15%, transparent)'
                          : 'color-mix(in srgb, var(--muted-foreground) 15%, transparent)',
                        color: link.permission === 'download' ? '#3b82f6' : 'var(--muted-foreground)',
                        textTransform: 'uppercase',
                      }}>
                        {link.permission === 'download' ? <><Edit size={8} /> Download</> : <><Eye size={8} /> View</>}
                      </span>
                      {link.hasPassword && <span title="Password protected"><Lock size={9} /></span>}
                      {link.expiresAt && (
                        <span>Expires {new Date(link.expiresAt).toLocaleDateString()}</span>
                      )}
                      {link.maxDownloads && (
                        <span>{link.downloadCount}/{link.maxDownloads} downloads</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => copyLink(link)}
                    title="Copy link"
                    style={{
                      padding: 5, borderRadius: 6, border: 'none', background: 'transparent',
                      color: copiedId === link.id ? 'var(--primary)' : 'var(--muted-foreground)',
                      cursor: 'pointer', display: 'flex',
                    }}
                  >
                    {copiedId === link.id ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                  <button
                    onClick={() => handleRevoke(link.id)}
                    title="Revoke link"
                    style={{
                      padding: 5, borderRadius: 6, border: 'none', background: 'transparent',
                      color: '#ef4444', cursor: 'pointer', display: 'flex',
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
